import type { ArenaWaveEntry, EnemyType, TowerType } from './types';

/**
 * The Test Arena's tower challenge.
 *
 * Send ONE arena wave of exactly 100 Shrekbruits and nothing else, kill every
 * one of them, and Shrekbook Hambi unlocks permanently.
 *
 * Why counting kills is the whole detector: the arena spawns exactly what the
 * wave committed, and every enemy ends either killed or leaked past the exit. So
 * "all 100 defeated" needs no leak-watching — if even one leaked, the kill count
 * at wave-clear is below 100.
 *
 * Pure: no Phaser, no storage, no sim access. GameScene feeds it the events the
 * sim already emits and persists the reward on its own; GameSim stays a
 * simulation that knows nothing about unlocks.
 */
export const SHREK_CHALLENGE: {
  readonly enemyType: EnemyType;
  readonly count: number;
  readonly reward: TowerType;
} = {
  enemyType: 'shrekbruit',
  count: 100,
  reward: 'shrekbook',
};

export class ArenaChallenge {
  private armed = false;
  private kills = 0;

  /**
   * Arm (or disarm) from the wave about to be sent. A qualifying wave is a
   * SINGLE entry of exactly `SHREK_CHALLENGE.count` Shrekbruits: 99, 101 and any
   * wave carrying a second enemy type all leave the tracker disarmed, so they
   * simply never complete.
   */
  armFor(entries: readonly ArenaWaveEntry[]): void {
    this.kills = 0;
    this.armed =
      entries.length === 1 &&
      entries[0].enemyType === SHREK_CHALLENGE.enemyType &&
      entries[0].count === SHREK_CHALLENGE.count;
  }

  /** Count a kill from an `enemyKilled` event (ignored unless armed and matching). */
  onKill(enemyType: EnemyType): void {
    if (!this.armed) return;
    if (enemyType !== SHREK_CHALLENGE.enemyType) return;
    this.kills++;
  }

  /**
   * Called on `waveCleared`: true when the armed wave was fully defeated.
   * Disarms either way — a challenge wave is judged once, and the next attempt
   * must arm itself through `armFor` again.
   */
  completedOnClear(): boolean {
    const won = this.armed && this.kills >= SHREK_CHALLENGE.count;
    this.armed = false;
    this.kills = 0;
    return won;
  }

  /** Abandon the attempt (Clear Wave, or leaving the arena). */
  reset(): void {
    this.armed = false;
    this.kills = 0;
  }

  /** Kills banked toward the current attempt (0 when disarmed). */
  get progress(): number {
    return this.armed ? this.kills : 0;
  }

  /** Whether a qualifying wave is currently in flight. */
  get isArmed(): boolean {
    return this.armed;
  }
}
